import * as React from 'react'
const { useState, useEffect } = React
import {
  Card,
  Typography,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Alert,
  Spin,
  Divider,
  Row,
  Col,
  Tag,
  Timeline,
  Space,
  message,
  Modal,
  Popover
} from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import planService from '../../services/planService'
import { PlanogramPlan, Milestone, } from '../../types/plan'
import dayjs from 'dayjs'

const { Title, Text, } = Typography
const { TextArea } = Input

const PlanActivation: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  // State management
  const [loading, setLoading] = useState(false)
  const [activating, setActivating] = useState(false)
  const [plan, setPlan] = useState<PlanogramPlan | null>(null)
  const [milestones, setMilestones] = useState<Milestone[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)


  // Load plan details and assignment data
  useEffect(() => {
    if (id) {
      loadPlanData()
    }
  }, [id])

  const loadPlanData = async () => {
    if (!id) return

    setLoading(true)
    try {
      // Load plan details
      const planResponse = await planService.getPlanById(id)
      if (planResponse.success && planResponse.data) {
        // Ensure required fields are present for type compatibility
        const planData = {
          ...planResponse.data,
          gmtCreate: planResponse.data.gmtCreate || new Date(),
          gmtModified: planResponse.data.gmtModified || new Date()
        }
        setPlan(planData)
        // Import mock milestones data
        const milestoneRes = await planService.getMilestonesByPlan(id);
        const planMilestones = milestoneRes.data
        setMilestones(planMilestones as unknown as Milestone[])
      } else {
        message.error(planResponse.errorMessage || t('planManagement.activation.messages.fetchFailed'))
        return
      }

      // Auto-validate on load
      // await performValidation()
    } catch (error) {
      console.error('Load plan data error:', error)
      message.error(t('planManagement.activation.messages.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleActivation = async (values: any) => {
    if (!id || !plan) return

    setActivating(true)
    try {
      const response = await planService.activatePlan(id, values)

      if (response.success) {
        message.success(t('planManagement.activation.messages.activateSuccess'))
        navigate('/plans')
      } else {
        message.error(response.errorMessage || t('planManagement.activation.messages.activateFailed'))
      }
    } catch (error) {
      console.error('Activation error:', error)
      message.error(t('planManagement.activation.messages.activateFailed'))
    } finally {
      setActivating(false)
      setShowConfirmModal(false)
    }
  }

  const handleConfirmActivation = () => {
    form.validateFields().then(() => {
      setShowConfirmModal(true)
    }).catch(info => {
      console.log('Validate Failed:', info)
    })
  }

  const renderTemplateInfo = () => {
    if (!plan) return null

    return (
      <Card 
        title={t('planManagement.activation.templateInfo.title')} 
        size="small" 
        style={{ 
          marginBottom: 16,
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}
      >
        <Row gutter={[16, 8]}>
          <Col span={8}>
            <Text strong>{t('planManagement.activation.templateInfo.templateReference')}</Text>
            <Text>{plan.templateReference}</Text>
          </Col>
          <Col span={8}>
            <Text strong>{t('planManagement.activation.templateInfo.planType')}</Text>
            <Tag color={getPlanTypeColor(plan.planType)}>{plan.planType}</Tag>
          </Col>
          {/* <Col span={8}>
            <Text strong>{t('planManagement.activation.templateInfo.businessActivity')}</Text>
            <Text>{plan.businessActivity}</Text>
          </Col> */}
          <Col span={8}>
            <Popover 
              content={
                <div style={{ maxWidth: 300, wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {plan.storeScope}
                </div>
              } 
              title={t('planManagement.activation.templateInfo.scope')} 
              trigger="hover"
            >
              <span style={{ cursor: 'pointer' }}>
                {plan.storeScope && plan.storeScope.length > 20 
                  ? `${plan.storeScope.substring(0, 20)}...` 
                  : plan.storeScope}
              </span>
            </Popover>
          </Col>
          <Col span={8}>
            <Text strong>{t('planManagement.activation.templateInfo.storeCount')}</Text>
            <Text>{plan.storeCount}{t('planManagement.activation.templateInfo.storeCountUnit')}</Text>
          </Col>
          <Col span={8}>
            <Text strong>{t('planManagement.activation.templateInfo.generateTime')}</Text>
            <Text>{dayjs(plan.planGenerateTime).format('YYYY-MM-DD HH:mm')}</Text>
          </Col>
        </Row>
      </Card>
    )
  }

  const renderMilestoneStructure = () => {
    if (!milestones.length) return null

    return (
      <Card 
        title={t('planManagement.activation.milestoneStructure.title')} 
        size="small" 
        style={{ 
          marginBottom: 16,
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}
      >
        <Timeline>
          {milestones.map((milestone) => (
            <Timeline.Item
              key={milestone.milestoneId}
              dot={milestone.finalMilestoneFlag ?
                <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                <ClockCircleOutlined style={{ color: '#1890ff' }} />
              }
            >
              <div>
                <Text strong>{milestone.milestoneName}</Text>
                {milestone.finalMilestoneFlag && <Tag color="green" style={{ marginLeft: 8 }}>{t('planManagement.activation.milestoneStructure.finalMilestone')}</Tag>}
                <br />
                <Text type="secondary">
                  {t('planManagement.activation.form.responsibleDepartment')}：{milestone.responsibleDepartment} |
                  {t('planManagement.activation.form.estimatedDuration')}：{milestone.templateDuration}{t('planManagement.activation.form.days')} |
                  {t('planManagement.activation.form.bufferTime')}：{milestone.templateBufferTime}{t('planManagement.activation.form.days')}
                </Text>
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </Card>
    )
  }

  const getPlanTypeColor = (type: string) => {
    const colors = {
      'Major': 'red',
      'Regular': 'blue',
      'Minor': 'green',
      'Urgent': 'orange',
      'Special': 'purple'
    }
    return colors[type as keyof typeof colors] || 'default'
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>{t('planManagement.activation.loading.loadingPlanData')}</Text>
        </div>
      </div>
    )
  }

  if (!plan) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message={t('planManagement.activation.loading.planNotFound')}
          description={t('planManagement.activation.loading.planNotFoundDesc')}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/planogram-plans')}>
              {t('planManagement.activation.loading.backToPlanList')}
            </Button>
          }
        />
      </div>
    )
  }

  if (plan.status !== 'Pending' && plan.status !== 'Paused') {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message={t('planManagement.activation.loading.planStatusIncorrect')}
          description={`${t('planManagement.activation.loading.onlyActivatePending')} ${plan.status}`}
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/planogram-plans')}>
              {t('planManagement.activation.loading.backToPlanList')}
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Page Header */}
      <div style={{ 
        backgroundColor: '#fff', 
        borderBottom: '1px solid #e5e7eb',
        padding: '24px 32px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
              <CalendarOutlined style={{ marginRight: '8px' }} />  {t('planManagement.activation.header.title')}
            </Title>
            <Text type="secondary" style={{ fontSize: '14px', marginTop: '4px' }}>
              {t('planManagement.activation.header.description')}
            </Text>
          </div>
          <Space>
            <Button
              size="large"
              onClick={() => navigate('/plans')}
              style={{
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={handleConfirmActivation}
              loading={activating}
              style={{
                borderRadius: '8px',
                backgroundColor: '#3b82f6',
                borderColor: '#3b82f6',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              {t('planManagement.activation.form.activatePlan')}
            </Button>
          </Space>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px 32px' }}>
        <Row gutter={[24, 24]}>
          <Col span={16}>
            {/* Template Information */}
            {renderTemplateInfo()}

            {/* Milestone Structure */}
            {renderMilestoneStructure()}

            {/* Activation Form */}
            <Card 
              title={t('planManagement.activation.activationConfig.title')}
              size="small"
              style={{
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleActivation}
                initialValues={{
                  plannedStartTime: dayjs(plan.plannedStartTime),
                  description: plan.description
                }}
              >               
                {/* <Form.Item
                  name="plannedStartTime"
                  label={t('planManagement.activation.form.plannedStartTime')}
                  rules={[{ required: true, message: t('planManagement.activation.form.selectPlannedStartTime') }]}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    placeholder={t('common.selectStartTimePlaceHolder')}
                    style={{ 
                      width: '100%',
                      borderRadius: '8px'
                    }}
                  />
                </Form.Item> */}

                <Form.Item
                  name="description"
                  label={t('planManagement.activation.form.planDescription')}
                >
                  <TextArea
                    rows={3}
                    placeholder={t('planManagement.activation.form.planDescriptionPlaceholder')}
                    maxLength={500}
                    showCount
                    style={{
                      borderRadius: '8px'
                    }}
                  />
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col span={8}>
         

            {/* Action Buttons */}
            <Card 
              size="small"
              style={{
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="dashed"
                  block
                  icon={<FileTextOutlined />}
                  onClick={() => navigate(`/assignment/${plan.id}`)}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {t('planManagement.activation.form.configurePersonnel')}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Confirmation Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14' }} />
            {t('planManagement.activation.confirm.title')}
          </Space>
        }
        open={showConfirmModal}
        onOk={() => form.submit()}
        onCancel={() => setShowConfirmModal(false)}
        confirmLoading={activating}
        okText={t('planManagement.activation.confirm.confirmActivate')}
        cancelText={t('common.cancel')}
        width={600}
        style={{
          borderRadius: '12px'
        }}
        bodyStyle={{
          padding: '24px'
        }}
      >
        <Alert
          message={t('planManagement.activation.confirm.description')}
          type="info"
          showIcon
          style={{ 
            marginBottom: 24,
            borderRadius: '8px'
          }}
        />

        <div>
          <p><Text strong>{t('planManagement.activation.confirm.planName')}:</Text>{plan.planName}</p>
          <p><Text strong>{t('planManagement.activation.confirm.planCode')}:</Text>{plan.planCode}</p>
          {/* <p><Text strong>项目负责人：</Text>{form.getFieldValue('projectOwner')}</p> */}
          <p><Text strong>{t('planManagement.activation.templateInfo.generateTime')}</Text>{form.getFieldValue('plannedStartTime')?.format('YYYY-MM-DD HH:mm')}</p>
          <p><Text strong>{t('planManagement.activation.confirm.affectedStores')}:</Text>{plan.storeCount} {t('planManagement.activation.confirm.storeUnit')}</p>
        </div>

        <Alert
          message={t('planManagement.activation.confirm.warning')}
          type="warning"
          showIcon
          style={{ 
            marginTop: 24,
            borderRadius: '8px'
          }}
        />
      </Modal>
    </div>
  )
}

export default PlanActivation